/**
 * =====================================================================================
 * SERVICIO DE FACTURAS ELECTRÓNICAS - facturas.service.js
 * =====================================================================================
 *
 * Este es el servicio más importante del sistema. Se encarga de:
 * 1. Crear facturas electrónicas (DTE)
 * 2. Consultar facturas existentes
 * 3. Anular facturas
 * 4. Generar identificadores únicos (UUID, número de control)
 * 5. Mapear datos del frontend al formato del backend
 *
 * DOCUMENTO TRIBUTARIO ELECTRÓNICO (DTE):
 * ---------------------------------------
 * Un DTE es un documento fiscal digital que reemplaza a los documentos en papel.
 * Tiene la misma validez legal que una factura impresa pero es:
 * - Generado electrónicamente
 * - Firmado digitalmente
 * - Transmitido al Ministerio de Hacienda para validación
 * - Almacenado en formato electrónico
 *
 * ESTRUCTURA DE UN DTE:
 * ---------------------
 *
 * ┌─────────────────────────────────────────────────────────────────┐
 * │                      IDENTIFICACIÓN                             │
 * │  - Código de generación (UUID único)                           │
 * │  - Número de control (DTE-01-XXXXXXXX-000000000000001)         │
 * │  - Tipo de DTE (01=Factura, 03=CCF)                            │
 * │  - Fecha y hora de emisión                                      │
 * │  - Ambiente (00=Pruebas, 01=Producción)                        │
 * └─────────────────────────────────────────────────────────────────┘
 *                              ↓
 * ┌─────────────────────────────────────────────────────────────────┐
 * │                         EMISOR                                   │
 * │  - NIT, NRC, Nombre, Dirección                                  │
 * │  - Actividad económica                                          │
 * │  (Datos de la empresa que emite la factura)                     │
 * └─────────────────────────────────────────────────────────────────┘
 *                              ↓
 * ┌─────────────────────────────────────────────────────────────────┐
 * │                        RECEPTOR                                  │
 * │  - Tipo documento (DUI/NIT), Número documento                   │
 * │  - Nombre, Dirección, Teléfono, Correo                          │
 * │  (Datos del cliente que recibe la factura)                      │
 * └─────────────────────────────────────────────────────────────────┘
 *                              ↓
 * ┌─────────────────────────────────────────────────────────────────┐
 * │                   CUERPO DEL DOCUMENTO                          │
 * │  Línea 1: Cantidad, Descripción, Precio, IVA                    │
 * │  Línea 2: Cantidad, Descripción, Precio, IVA                    │
 * │  ... (productos o servicios vendidos)                           │
 * └─────────────────────────────────────────────────────────────────┘
 *                              ↓
 * ┌─────────────────────────────────────────────────────────────────┐
 * │                         RESUMEN                                  │
 * │  - Totales: Gravada, Exenta, No sujeta                          │
 * │  - IVA total                                                     │
 * │  - Total a pagar (en números y letras)                          │
 * │  - Forma de pago                                                 │
 * └─────────────────────────────────────────────────────────────────┘
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * - api.js: Cliente HTTP para las peticiones
 * - src/views/emision/FacturaView.vue: Formulario de emisión que llama create()
 * - src/store/modules/documentos.js: Podría usar este servicio
 * - Backend: POST /api/facturas acepta el DTO mapeado por mapearParaAPI()
 *
 * =====================================================================================
 */

// Importamos la instancia de Axios configurada
import api from './api'

/**
 * =====================================================================================
 * FUNCIÓN: generarUUID
 * =====================================================================================
 *
 * Genera un UUID (Universally Unique Identifier) versión 4.
 * El UUID es un identificador de 128 bits representado como 32 caracteres hexadecimales.
 *
 * ¿QUÉ ES UN UUID?
 * ----------------
 * Es un número único que identifica algo de forma universal. La probabilidad de
 * generar dos UUIDs iguales es prácticamente cero (1 en 2^122).
 *
 * FORMATO: XXXXXXXX-XXXX-4XXX-YXXX-XXXXXXXXXXXX
 * - El "4" indica que es versión 4 (aleatorio)
 * - La "Y" es 8, 9, A o B (variante)
 *
 * EJEMPLO: A1B2C3D4-E5F6-4789-A012-B34567890ABC
 *
 * USO EN DTE:
 * -----------
 * El Ministerio de Hacienda requiere que cada documento tenga un "código de generación"
 * que es un UUID único. Este código identifica el documento de forma única en todo
 * el sistema tributario del país.
 *
 * NOTA: El MH requiere el UUID en MAYÚSCULAS
 *
 * @returns {string} UUID en formato mayúsculas
 */
function generarUUID() {
  // El método replace() con regex /[xy]/g busca todas las 'x' e 'y' en la plantilla
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    // Math.random() genera número entre 0 y 1
    // Multiplicamos por 16 para obtener 0-15 (valores hex)
    // El operador | 0 trunca a entero (más rápido que Math.floor)
    const r = Math.random() * 16 | 0

    // Si el caracter es 'x', usamos r directamente
    // Si es 'y', aplicamos la fórmula de variante UUID: (r & 0x3 | 0x8)
    // Esto asegura que y sea 8, 9, A o B
    const v = c === 'x' ? r : (r & 0x3 | 0x8)

    // Convertimos a hexadecimal (base 16)
    return v.toString(16)
  }).toUpperCase() // El MH requiere mayúsculas
}

/**
 * =====================================================================================
 * FUNCIÓN: generarNumeroControl
 * =====================================================================================
 *
 * Genera el número de control del documento según el formato requerido por Hacienda.
 *
 * FORMATO: DTE-TT-CCCCCCCC-NNNNNNNNNNNNNNN
 *
 * Donde:
 * - DTE: Prefijo fijo que indica "Documento Tributario Electrónico"
 * - TT: Tipo de documento (01=Factura, 03=CCF, etc.)
 * - CCCCCCCC: Código de establecimiento (8 caracteres alfanuméricos)
 * - NNNNNNNNNNNNNNN: Número correlativo (15 dígitos)
 *
 * EJEMPLOS:
 * - DTE-01-M001P001-000000000000001 (Primera factura del punto de venta M001P001)
 * - DTE-03-SUCUR001-000000000000042 (CCF #42 de sucursal SUCUR001)
 *
 * CÓDIGO DE ESTABLECIMIENTO:
 * --------------------------
 * Identifica de forma única cada punto de emisión de la empresa.
 * Normalmente tiene formato como:
 * - M001P001: Matriz 001, Punto de venta 001
 * - SUCUR001: Sucursal 001
 *
 * @param {string} tipoDte - Tipo de DTE ("01", "03", etc.)
 * @param {string} codigoEstablecimiento - Código del punto de venta
 * @param {number} correlativo - Número secuencial del documento
 * @returns {string} Número de control formateado
 */
function generarNumeroControl(tipoDte = '01', codigoEstablecimiento = 'M001P001', correlativo = 1) {
  // Limpiar el código de establecimiento:
  // 1. Eliminar caracteres no alfanuméricos con regex
  // 2. Rellenar con ceros si es muy corto (padEnd)
  // 3. Truncar a 8 caracteres si es muy largo (substring)
  // 4. Convertir a mayúsculas
  const codigo = (codigoEstablecimiento || 'M001P001')
    .replace(/[^A-Z0-9]/gi, '')  // gi = global, case insensitive
    .padEnd(8, '0')               // Rellena con '0' hasta 8 caracteres
    .substring(0, 8)              // Toma solo los primeros 8
    .toUpperCase()

  // El correlativo debe ser un string de 15 dígitos, rellenado con ceros a la izquierda
  // padStart() agrega caracteres al inicio del string
  const corr = correlativo.toString().padStart(15, '0')

  // Concatenar usando template literal
  return `DTE-${tipoDte}-${codigo}-${corr}`
}

/**
 * =====================================================================================
 * FUNCIÓN: buscarIdPorCodigo
 * =====================================================================================
 *
 * Busca el ID de base de datos de un catálogo dado su código del MH.
 *
 * PROBLEMA QUE RESUELVE:
 * ----------------------
 * El Ministerio de Hacienda usa CÓDIGOS (strings como "01", "02")
 * Nuestra base de datos usa IDs numéricos (1, 2, 3...)
 *
 * Los catálogos tienen ambos:
 * { id: 1, codigo: "01", valor: "Efectivo" }
 *
 * Cuando el usuario selecciona "Efectivo" (código "01"), necesitamos
 * encontrar el ID (1) para guardar en la factura.
 *
 * @param {Array} catalogo - Array de objetos {id, codigo, valor}
 * @param {string} codigo - Código MH a buscar (ej: "01")
 * @returns {number|null} ID encontrado o null
 *
 * EJEMPLO:
 * --------
 * const formasPago = [{id:1, codigo:"01", valor:"Efectivo"}, {id:2, codigo:"02", valor:"Tarjeta"}]
 * buscarIdPorCodigo(formasPago, "02") // Retorna: 2
 */
function buscarIdPorCodigo(catalogo, codigo) {
  // Validación de parámetros
  if (!catalogo || !codigo) return null

  // find() retorna el primer elemento que cumple la condición, o undefined
  const item = catalogo.find(c => c.codigo === codigo)

  // Optional chaining (?.) retorna undefined si item es null/undefined
  // El || null convierte undefined a null
  return item?.id || null
}

/**
 * =====================================================================================
 * FUNCIÓN: mapearParaAPI
 * =====================================================================================
 *
 * Esta es la función más compleja del servicio. Transforma los datos del formulario
 * del frontend al formato DTO (Data Transfer Object) que espera el backend.
 *
 * IMPORTANTE: Esta función implementa DOS SETS DE FÓRMULAS SEPARADAS:
 *
 * SET A - FACTURA (DTE-01): Consumidor Final
 * ------------------------------------------
 * - El precio unitario YA viene con IVA incluido desde CuerpoDocumento.vue
 *   (se agrega al momento de seleccionar el producto para venta gravada)
 * - precioUnitario = precioBase * 1.13 (hecho en el frontend al seleccionar)
 * - ventaGravada se envía CON IVA incluido
 * - ivaItem se calcula de forma INVERSA: (ventaGravada / 1.13) * 0.13
 * - El totalIva es SOLO INFORMATIVO, NO se suma al final
 * - montoTotalOperacion = subTotal (sin sumar IVA adicional)
 * - tributos: null para IVA (según MH, no se incluye "20" en Factura)
 *
 * SET B - CRÉDITO FISCAL (DTE-03): Contribuyente
 * ----------------------------------------------
 * - El precio unitario es NETO (sin IVA)
 * - ventaGravada es el monto SIN IVA
 * - ivaItem se calcula DIRECTO: ventaGravada * 0.13
 * - El totalIva SÍ se suma al final
 * - montoTotalOperacion = subTotal + totalIva
 * - tributos: array con código "20" para IVA
 *
 * @param {Object} documento - Datos del documento del frontend
 * @param {number} sucursalId - ID de la sucursal emisora
 * @param {string} ambiente - "00" pruebas, "01" producción
 * @param {Object} establecimiento - Datos del punto de venta
 * @param {Object} catalogos - Catálogos para mapear códigos a IDs
 * @returns {Object} DTO listo para enviar al backend
 */
function mapearParaAPI(documento, sucursalId, ambiente = '00', establecimiento = null, catalogos = {}) {
  // ===========================================
  // PASO 1: DETERMINAR TIPO DE DOCUMENTO
  // ===========================================
  // Esta variable es CRÍTICA - determina qué set de fórmulas usar
  const tipoDte = documento.tipoDte || '01'
  const esFactura = tipoDte === '01'  // true = Factura Consumidor Final
  const esCCF = tipoDte === '03'      // true = Crédito Fiscal

  // ===========================================
  // PASO 2: FECHA Y HORA DE EMISIÓN
  // ===========================================
  // IMPORTANTE: Usar métodos locales en lugar de toISOString() que devuelve UTC
  // Esto evita que la fecha se interprete como "futura" cuando es de noche
  const ahora = new Date()
  const fechaEmision = documento.fechaEmision ||
    `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}-${String(ahora.getDate()).padStart(2, '0')}`
  const horaEmision = documento.horaEmision ||
    `${String(ahora.getHours()).padStart(2, '0')}:${String(ahora.getMinutes()).padStart(2, '0')}:${String(ahora.getSeconds()).padStart(2, '0')}`

  // ===========================================
  // PASO 3: GENERAR IDENTIFICADORES ÚNICOS
  // ===========================================
  const codigoGeneracion = generarUUID()
  const codigoEstablecimiento = establecimiento?.codigo
    || establecimiento?.codigoEstablecimiento
    || establecimiento?.codigoPuntoVenta
    || 'M001P001'
  const correlativoTemporal = Date.now() % 999999999999999
  const numeroControl = generarNumeroControl(tipoDte, codigoEstablecimiento, correlativoTemporal)

  // ===========================================
  // PASO 3.5: RESOLVER ID DE UNIDAD DE MEDIDA "OTRA" (Codigo MH: 99)
  // ===========================================
  // La tabla cat_uni_medida tiene Id ≠ Codigo. Debemos usar el Id (FK),
  // no el Codigo MH, al enviar al backend.
  const unidadOtraId = (catalogos.unidadesMedida || []).find(u =>
    String(u.codigo) === '99'
  )?.id || null

  // ===========================================
  // PASO 4: FILTRAR ITEMS POR TIPO
  // ===========================================
  const items = documento.items || []
  const productosItems = items.filter(item => item.tipoItem === 'producto')
  const contribucionesItems = items.filter(item => item.tipoItem === 'contribucion')
  const noAfectosItems = items.filter(item => item.tipoItem === 'noAfecto')

  // ===========================================
  // PASO 4.5: HELPER PARA VINCULAR ITEMS CON DOCUMENTOS RELACIONADOS
  // ===========================================
  // Según MH: cada ítem puede vincularse a un documento relacionado específico
  // mediante el campo "numeroDocumento" que contiene el UUID o correlativo
  // del documento al que hace referencia.
  // Ejemplo: En una NC que ajusta 3 CCFs, cada ítem indica a cuál CCF pertenece.
  const rawDocRel = documento.documentosRelacionados || []
  const documentosRelacionados = Array.isArray(rawDocRel) ? rawDocRel : (rawDocRel.relacionados || [])

  /**
   * Obtiene el numeroDocumento del documento relacionado dado su ID interno.
   * Si no se especifica ID pero hay docs relacionados, usa el primero (MH lo requiere).
   * @param {string|null} documentoRelacionadoId - ID interno del documento (UUID de UI)
   * @returns {string|null} - El numeroDocumento del doc relacionado, o null si no hay docs
   */
  const obtenerNumeroDocumentoRelacionado = (documentoRelacionadoId) => {
    if (!documentoRelacionadoId) return null
    const docRelacionado = documentosRelacionados.find(doc => doc.id === documentoRelacionadoId)
    return docRelacionado?.numeroDocumento || null
  }

  // ===========================================
  // PASO 5: MAPEAR ITEMS PRIMERO PARA CALCULAR TOTALES EXACTOS
  // ===========================================
  // Mapeamos los items primero para poder sumar los valores redondeados
  // y así evitar discrepancias entre la suma de items y los totales del resumen

  const productosItemsMapeados = productosItems.map((item, index) => {
    const precioBase = item.precioUnitario || 0
    const cantidad = item.cantidad || 0
    const descuento = item.descuento || 0

    let precioUnitarioFinal = precioBase
    let ventaGravadaItem = 0
    let ventaExentaItem = 0
    let ventaNoSujItem = 0
    let ivaItem = 0
    let tributosItem = null
    let montoDescuentoItem = descuento

    // Convertir tipoVenta a número para evitar problemas de comparación string vs number
    const tipoVentaNum = parseInt(item.tipoVenta) || 1 // Default a gravada si no está definido

    if (esFactura) {
      // ═══════════════════════════════════════════════════════════════
      // FACTURA (DTE-01): Precios CON IVA incluido
      // ═══════════════════════════════════════════════════════════════
      // Según documentación del MH:
      // - precioUni = precio CON IVA (lo que ve el consumidor final)
      // - ventaGravada = (precioUni × cantidad) - descuento = CON IVA incluido
      // - ivaItem = (ventaGravada / (1 + %IVA)) × %IVA (cálculo INVERSO)
      if (tipoVentaNum === 1) {
        // Venta GRAVADA
        // Usar porcentajeIVA del producto (por defecto 13%)
        const porcentajeIVA = (item.porcentajeIVA || 13) / 100
        const factorIVA = 1 + porcentajeIVA
        // precioUni = precio con IVA (lo que ve el cliente)
        // El precio YA viene con IVA incluido desde CuerpoDocumento.vue
        // (se agregó al seleccionar el producto para FACTURA gravada)
        precioUnitarioFinal = precioBase // NO multiplicar, ya tiene IVA
        // ventaGravada = valor CON IVA incluido (según documentación)
        ventaGravadaItem = (precioUnitarioFinal * cantidad) - descuento
        // IVA = cálculo INVERSO: sacar el IVA que lleva dentro
        ivaItem = (ventaGravadaItem / factorIVA) * porcentajeIVA
        // El descuento ya incluye IVA en Factura
        montoDescuentoItem = descuento
        // Tributos para FE: según MH, si solo aplica IVA → null
        // Si hay otros tributos (FOVIAL, COTRANS, etc.) → solo esos (sin "20")
        const tributosDelItem = item.tributos || []
        const otrosTributos = tributosDelItem.filter(t => t !== '20')
        tributosItem = otrosTributos.length > 0 ? otrosTributos : null
      } else if (tipoVentaNum === 2) {
        // Venta EXENTA (no tiene IVA)
        ventaExentaItem = (precioBase * cantidad) - descuento
        montoDescuentoItem = descuento
      } else if (tipoVentaNum === 3) {
        // Venta NO SUJETA (no tiene IVA)
        ventaNoSujItem = (precioBase * cantidad) - descuento
        montoDescuentoItem = descuento
      }
    } else if (esCCF) {
      // ═══════════════════════════════════════════════════════════════
      // CCF (DTE-03): Precios ya son NETOS
      // ═══════════════════════════════════════════════════════════════
      precioUnitarioFinal = precioBase
      const subtotalNeto = (precioBase * cantidad) - descuento
      if (tipoVentaNum === 1) {
        // Usar porcentajeIVA del producto (por defecto 13%)
        const porcentajeIVA = (item.porcentajeIVA || 13) / 100
        ventaGravadaItem = subtotalNeto
        ivaItem = subtotalNeto * porcentajeIVA
        // CCF: SÍ incluir '20' (IVA) en tributos del item según schema MH fe-ccf-v3.json
        // El schema MH requiere que items con ventaGravada > 0 tengan tributos con '20'
        // NOTA: El '20' se excluirá del tributosResumen para evitar doble conteo
        // en la fórmula del backend: montoTotalOperacion = subTotal + totalTributosSec1 + totalIva
        const tributosDelItem = item.tributos || []
        const otrosTributos = tributosDelItem.filter(t => t !== '20')
        tributosItem = ['20', ...otrosTributos]
      } else if (tipoVentaNum === 2) {
        ventaExentaItem = subtotalNeto
      } else if (tipoVentaNum === 3) {
        ventaNoSujItem = subtotalNeto
      }
    }

    // CUERPO: usar redondear8() para 8 decimales según MH (Sección XXI)
    return {
      numItem: index + 1,
      tipoItem: item.tipo || 1,
      // Vinculación con documento relacionado (para NC/ND que ajustan múltiples docs)
      // Según MH JSON schema: numeroDocumento es nullable
      numeroDocumento: nullIfEmpty(obtenerNumeroDocumentoRelacionado(item.documentoRelacionadoId)),
      cantidad: redondear8(cantidad),
      // Según MH JSON schema: codigo es nullable
      codigo: nullIfEmpty(item.codigo),
      codTributo: null,
      // Usar el Id de BD de la unidad de medida del item; fallback: servicios→"Otra", productos→"Unidad"
      uniMedida: parseInt(item.unidadMedida) || (item.tipo === 2 ? unidadOtraId : null) || 39,
      descripcion: item.descripcion,
      precioUni: redondear8(precioUnitarioFinal),
      // PSV: Precio Sugerido de Venta - si no aplica enviar 0 según MH
      psv: item.psv ? redondear8(item.psv) : 0,
      // Backend DTO usa "MontoDescuento" (camelCase: "montoDescuento")
      montoDescuento: redondear8(montoDescuentoItem),
      ventaGravada: redondear8(ventaGravadaItem),
      ventaExenta: redondear8(ventaExentaItem),
      ventaNoSuj: redondear8(ventaNoSujItem),
      tributos: tributosItem,
      // noGravado: Cargos/Abonos que no afectan base imponible - null para productos normales
      // IMPORTANTE: Enviar null (no 0) porque el backend valida NoGravado.HasValue
      // Si enviamos 0, el backend considera que el item tiene NoGravado y aplica
      // la regla "items con NoGravado no pueden tener tributos"
      noGravado: null,
      ivaItem: redondear8(ivaItem),
      _tipoVenta: item.tipoVenta,
      // ================================================
      // CONFIGURACIÓN DE TRIBUTOS ADICIONALES (Sección 1)
      // ================================================
      // Preservar la configuración de valores que el usuario definió
      // para tributos como FOVIAL, Turismo, CO-TRANS, etc.
      _tributosAdicionalesConfig: item.tributosAdicionalesConfig || {},
      // Base para cálculo de tributos Sección 1 (sin IVA para Factura)
      _baseParaTributos: esFactura ? redondear8(ventaGravadaItem / 1.13) : redondear8(ventaGravadaItem),
      // ================================================
      // INTEGRACIÓN CON INVENTARIO
      // ================================================
      // Estos campos permiten al backend descontar del inventario
      // REGLA: productoId y bodegaId deben especificarse juntos o ambos ser null
      productoId: item.productoId || null,
      bodegaId: item.productoId ? (item.bodegaId || null) : null,
      costoUnitario: item.productoId ? (item.costoUnitario ? redondear8(item.costoUnitario) : null) : null
    }
  })

  // Mapear contribuciones
  const contribucionesItemsMapeados = contribucionesItems.map((item, index) => {
    const montoOriginal = Math.abs(item.monto || 0)
    const tipoVenta = item.tipoVenta || 3

    let precioUniContribucion = montoOriginal
    let ventaGravadaContribucion = 0
    let ventaExentaContribucion = 0
    let ventaNoSujContribucion = 0
    let ivaItem = 0
    let tributosContribucion = null

    if (esFactura) {
      // Para Factura, el monto ingresado incluye IVA si es gravada
      // IMPORTANTE: Según JSON schema FE, para tipoItem=4 el campo tributos debe ser null
      if (tipoVenta === 1) {
        // Contribución GRAVADA: va CON IVA incluido
        tributosContribucion = null  // FE con tipoItem=4: tributos debe ser null (MH JSON schema)
        // El monto original incluye IVA
        precioUniContribucion = montoOriginal
        // ventaGravada = valor CON IVA incluido (según documentación)
        ventaGravadaContribucion = montoOriginal
        // IVA = cálculo INVERSO: sacar el IVA que lleva dentro
        ivaItem = (ventaGravadaContribucion / 1.13) * 0.13
      } else if (tipoVenta === 2) {
        ventaExentaContribucion = montoOriginal
      } else if (tipoVenta === 3) {
        ventaNoSujContribucion = montoOriginal
      }
    } else if (esCCF) {
      // Para CCF, el monto ya es NETO
      if (tipoVenta === 1) {
        tributosContribucion = ['20']
        ventaGravadaContribucion = montoOriginal
        ivaItem = montoOriginal * 0.13
      } else if (tipoVenta === 2) {
        ventaExentaContribucion = montoOriginal
      } else if (tipoVenta === 3) {
        ventaNoSujContribucion = montoOriginal
      }
    }

    // CUERPO: usar redondear8() para 8 decimales según MH (Sección XXI)
    return {
      numItem: productosItems.length + index + 1,
      tipoItem: 4,
      // Vinculación con documento relacionado (para NC/ND que ajustan múltiples docs)
      // Según MH JSON schema: numeroDocumento es nullable
      numeroDocumento: nullIfEmpty(obtenerNumeroDocumentoRelacionado(item.documentoRelacionadoId)),
      cantidad: 1,
      // Según MH JSON schema: codigo y codTributo son nullable
      codigo: nullIfEmpty(item.tributo),
      codTributo: nullIfEmpty(item.tributo),
      uniMedida: unidadOtraId || 39,
      descripcion: item.descripcion || `Impuesto especial - ${item.tributo}`,
      precioUni: redondear8(precioUniContribucion),
      // PSV: no aplica para contribuciones - enviar 0 según MH
      psv: 0,
      montoDescuento: 0, // Backend DTO espera "montoDescuento"
      ventaGravada: redondear8(ventaGravadaContribucion),
      ventaExenta: redondear8(ventaExentaContribucion),
      ventaNoSuj: redondear8(ventaNoSujContribucion),
      tributos: tributosContribucion,
      // noGravado: MH requiere este campo como number (no nullable) para CCF - enviar 0 para contribuciones
      noGravado: 0,
      ivaItem: redondear8(ivaItem),
      _tipoVenta: tipoVenta
    }
  })

  // Mapear items No Afectos (Cargos/Abonos que no afectan la base imponible)
  // Según especificación MH: van como ítems con valores especiales
  const noAfectosItemsMapeados = noAfectosItems.map((item, index) => {
    // El monto puede ser positivo (cargo) o negativo (abono)
    const monto = item.tipoCargoAbono === 'abono'
      ? -Math.abs(item.monto || 0)
      : Math.abs(item.monto || 0)

    // CUERPO: usar redondear8() para 8 decimales según MH (Sección XXI)
    return {
      numItem: productosItems.length + contribucionesItems.length + index + 1,
      tipoItem: item.tipo || 3, // 3 = Ambos (bien y servicio)
      // Vinculación con documento relacionado (para NC/ND que ajustan múltiples docs)
      // Según MH JSON schema: numeroDocumento es nullable
      numeroDocumento: nullIfEmpty(obtenerNumeroDocumentoRelacionado(item.documentoRelacionadoId)),
      cantidad: 1,
      // Según MH JSON schema: codigo es nullable
      codigo: nullIfEmpty(item.codigo),
      codTributo: null, // Tributo sujeto a IVA: null
      uniMedida: unidadOtraId || 39, // Unidad de medida especial para no afectos
      descripcion: item.descripcion || 'Cargo/Abono no afecto',
      precioUni: 0, // Precio Unitario: $0.00
      // PSV: no aplica para cargos/abonos - enviar 0 según MH
      psv: 0,
      montoDescuento: 0, // Backend DTO espera "montoDescuento"
      ventaGravada: 0, // Ventas Gravadas: $0.00
      ventaExenta: 0, // Ventas Exentas: $0.00
      ventaNoSuj: 0, // Ventas No Sujetas: $0.00
      tributos: null, // Sin tributos
      ivaItem: 0, // Sin IVA
      noGravado: redondear8(monto), // Cargos/Abonos que no afectan base imponible
      _tipoVenta: 0 // Tipo especial para no afectos
    }
  })

  // Combinar todos los items
  const todosLosItems = [...productosItemsMapeados, ...contribucionesItemsMapeados, ...noAfectosItemsMapeados]

  // ===========================================
  // PASO 6: CALCULAR TOTALES DESDE ITEMS MAPEADOS
  // ===========================================
  // Sumamos los valores YA REDONDEADOS de los items para que coincidan exactamente

  let totalGravadaBruta = 0
  let totalExentaBruta = 0
  let totalNoSujBruta = 0
  let totalIvaItems = 0

  todosLosItems.forEach(item => {
    totalGravadaBruta += item.ventaGravada
    totalExentaBruta += item.ventaExenta
    totalNoSujBruta += item.ventaNoSuj
    totalIvaItems += item.ivaItem
  })

  // Redondear los totales acumulados
  totalGravadaBruta = redondear(totalGravadaBruta)
  totalExentaBruta = redondear(totalExentaBruta)
  totalNoSujBruta = redondear(totalNoSujBruta)
  totalIvaItems = redondear(totalIvaItems)

  // ===========================================
  // PASO 7: DESCUENTOS GLOBALES
  // ===========================================
  const descuentosGlobales = documento.descuentosGlobales || {}
  // Los descuentos globales para Factura vienen CON IVA incluido
  const descGravadaConIva = redondear(descuentosGlobales.montoGravado || 0)
  const descExenta = redondear(descuentosGlobales.montoExento || 0)
  const descNoSuj = redondear(descuentosGlobales.montoNoSujeto || 0)

  // ===========================================
  // PASO 8: CÁLCULO DE TOTALES SEGÚN TIPO DTE
  // ===========================================
  // Según documentación del MH:
  // FACTURA (DTE-01):
  //   - totalGravada = sum(item.ventaGravada) = CON IVA incluido
  //   - totalIva = Σ(ivaItem) - ivaDescuentoGlobal (INFORMATIVO)
  //   - subTotalVentas = totalGravada + totalExenta + totalNoSuj (CON IVA)
  //   - subTotal = subTotalVentas - descuentos
  //   - montoTotalOperacion = subTotal (no se suma IVA adicional)

  // totalGravada = suma de ventaGravada de items (CON IVA para Factura)
  let totalGravada = totalGravadaBruta
  let totalExenta = totalExentaBruta
  let totalNoSuj = totalNoSujBruta

  // totalIva = suma de ivaItem de items (ya calculado)
  let totalIva = totalIvaItems

  // Descuentos globales
  let descGravada = 0
  let subTotal = 0
  let montoTotalOperacion = 0
  let ivaNeto = 0 // IVA neto después de descuento (para CCF y referencia)

  // ===========================================
  // PASO 8.1: CAMPOS ADICIONALES SEGÚN MH (Sección XVII)
  // ===========================================
  // subTotalVentas: suma de operaciones sin descuentos (requerido por MH)
  let subTotalVentas = redondear(totalGravada + totalExenta + totalNoSuj)

  // Suma de descuentos por ítem (para calcular totalDescu)
  const totalDescuItems = redondear(todosLosItems.reduce((sum, item) => sum + (item.montoDescuento || 0), 0))

  // porcentajeDescuento: viene del documento o es 0 (sin símbolo %)
  const porcentajeDescuento = redondear(documento.descuentosGlobales?.porcentaje || 0)

  if (esFactura) {
    // ═══════════════════════════════════════════════════════════════
    // FACTURA (DTE-01): Items tienen valores CON IVA incluido
    // ═══════════════════════════════════════════════════════════════
    // Según documentación:
    // - El descuento global viene CON IVA incluido
    // - subTotalVentas = totalGravada + totalExenta + totalNoSuj (CON IVA)
    // - subTotal = subTotalVentas - descuentos
    // - totalIva = Σ(ivaItem) - ivaDescuentoGlobal
    descGravada = descGravadaConIva  // CON IVA incluido

    // subTotalVentas = totalGravada + totalExenta + totalNoSuj (según doc: CON IVA)
    const subTotalVentas = redondear(totalGravada + totalExenta + totalNoSuj)

    // subTotal = subTotalVentas - descuentos (según doc)
    subTotal = redondear(subTotalVentas - descGravada - descExenta - descNoSuj)

    // IVA del descuento global: (descuGravada / 1.13) × 0.13
    const ivaDescuentoGlobal = redondear((descGravada / 1.13) * 0.13)

    // totalIva = Σ(ivaItem) - ivaDescuentoGlobal (según doc)
    totalIva = redondear(totalIvaItems - ivaDescuentoGlobal)
    ivaNeto = totalIva // Para Factura, guardar referencia

    // montoTotalOperacion se calculará después con la fórmula MH Campo 146
    // Por ahora, inicializar con subTotal (se ajustará con tributos después)
    montoTotalOperacion = subTotal

  } else if (esCCF) {
    // ═══════════════════════════════════════════════════════════════
    // CCF (DTE-03): Ya son valores NETOS
    // ═══════════════════════════════════════════════════════════════
    descGravada = descGravadaConIva  // Para CCF el descuento ya es neto

    // Netos después de descuentos
    const gravadaNeta = redondear(totalGravada - descGravada)
    const exentaNeta = redondear(totalExenta - descExenta)
    const noSujNeta = redondear(totalNoSuj - descNoSuj)

    // IVA del descuento global: descuGravada × 0.13
    const ivaDescuento = redondear(descGravada * 0.13)
    ivaNeto = redondear(totalIva - ivaDescuento)

    // CCF: totalIva = Σ(ivaItem) - ivaDescuentoGlobal (igual que Factura)
    totalIva = ivaNeto

    // SubTotal es neto (sin IVA)
    subTotal = redondear(gravadaNeta + exentaNeta + noSujNeta)

    // montoTotalOperacion se calculará después según MH Campo 146:
    // montoTotalOperacion = subTotal + Σ(tributos.valor)
    // donde tributos incluye '20' (IVA) para CCF
    // Por ahora, solo inicializar con subTotal
    montoTotalOperacion = subTotal
  }

  // ===========================================
  // PASO 9: ITEMS NO AFECTOS Y CONTRIBUCIONES
  // ===========================================
  const totalNoAfectos = items.reduce((sum, item) => {
    if (item.tipoItem === 'noAfecto') {
      const monto = Math.abs(item.monto || 0)
      return item.tipoCargoAbono === 'abono' ? sum - monto : sum + monto
    }
    return sum
  }, 0)

  const totalContribuciones = contribucionesItems.reduce((sum, item) => {
    return sum + Math.abs(item.monto || 0)
  }, 0)

  // Retenciones
  const ivaRetenido = redondear(documento.ivaRetenido || 0)
  const ivaPercibido = redondear(documento.ivaPercibido || 0)
  const retencionRenta = redondear(documento.retencionRenta || 0)

  const totalNoGravado = redondear(totalNoAfectos)

  // ===========================================
  // PASO 10: TOTAL A PAGAR - SEPARADO POR TIPO DTE
  // ===========================================
  let totalPagar = 0

  if (esFactura) {
    // FACTURA: totalPagar = montoTotalOperacion + totalNoGravado - retenciones
    totalPagar = redondear(montoTotalOperacion + totalNoGravado - ivaRetenido - retencionRenta)
  } else if (esCCF) {
    // CCF: totalPagar incluye IVA
    totalPagar = redondear(montoTotalOperacion + totalNoGravado - ivaRetenido + ivaPercibido - retencionRenta)
  }

  // ===========================================
  // PASO 11: LIMPIAR CAMPOS INTERNOS DE ITEMS
  // ===========================================
  const cuerpoDocumento = todosLosItems.map(item => {
    // Eliminar el campo interno _tipoVenta que usamos para cálculos
    const { _tipoVenta, ...itemLimpio } = item
    return itemLimpio
  })

  // ===========================================
  // PASO 12: MAPEAR PAGOS PARA EL BACKEND
  // ===========================================
  // El backend espera CreatePagoDto con:
  // - catFormaPagoId (int): ID de la forma de pago en BD
  // - monto (decimal): Monto del pago
  // - referencia (string?): Número de referencia
  // - catPlazoId (int?): ID del plazo en BD
  // - periodo (decimal?): Cantidad de periodos
  //
  // Reglas importantes:
  // - El monto siempre debe ser el valor real del pago (incluso en crédito)
  // - referencia puede ser null para efectivo (código 01)
  // - catPlazoId y periodo son requeridos si condicionOperacion = 2 (crédito)
  const condicionOp = documento.condicionOperacion || 1
  const tieneSaldoFavor = documento.saldoFavor && documento.saldoFavor > 0

  // Obtener catálogos para conversión código -> ID
  const { formasPago: catalogoFormasPago = [], plazos: catalogoPlazos = [] } = catalogos

  let pagos = (documento.pagos || []).map(pago => {
    // ===========================================
    // Determinar catFormaPagoId
    // ===========================================
    // El pago puede venir con catFormaPagoId (si viene de prepararPagosParaAPI)
    // o con codigo (si viene de otro lugar)
    let catFormaPagoId = pago.catFormaPagoId

    if (!catFormaPagoId && pago.codigo) {
      // Buscar ID por código en el catálogo
      const formaPago = catalogoFormasPago.find(f => f.codigo === pago.codigo)
      catFormaPagoId = formaPago?.id || parseInt(pago.codigo, 10) || 1
    }

    // Si el receptor tiene saldo a favor, usar forma de pago 99 (Otros)
    // Según MH CAT-017: código 99 = "Otros (se debe indicar el medio de pago)"
    if (tieneSaldoFavor) {
      const formaPagoOtro = catalogoFormasPago.find(f => f.codigo === '99')
      catFormaPagoId = formaPagoOtro?.id || 99
    }

    // Fallback: si aún no tenemos ID, usar 1 (Efectivo)
    catFormaPagoId = catFormaPagoId || 1

    // ===========================================
    // Determinar monto
    // ===========================================
    // El monto siempre debe ser el valor real del pago
    let montoPagoValor = redondear(pago.monto || pago.montoPago || 0)

    // ===========================================
    // Determinar referencia
    // ===========================================
    // Para efectivo (catFormaPagoId = 1) o crédito, referencia puede ser null
    // Según MH JSON schema: referencia es nullable
    let referenciaPago = nullIfEmpty(pago.referencia)
    if (catFormaPagoId === 1 || condicionOp === 2) {
      referenciaPago = nullIfEmpty(pago.referencia)
    }

    // ===========================================
    // Determinar catPlazoId (solo para crédito)
    // ===========================================
    let catPlazoId = null
    if (condicionOp === 2) {
      // El pago puede venir con catPlazoId o con plazo (código)
      catPlazoId = pago.catPlazoId

      if (!catPlazoId && pago.plazo) {
        // Buscar ID por código en el catálogo
        const plazo = catalogoPlazos.find(p => p.codigo === pago.plazo)
        catPlazoId = plazo?.id || parseInt(pago.plazo, 10) || null
      }
    }

    // ===========================================
    // Determinar periodo (solo para crédito)
    // ===========================================
    let periodoPago = null
    if (condicionOp === 2 && pago.periodo) {
      periodoPago = parseFloat(pago.periodo) || null
    }

    return {
      catFormaPagoId: catFormaPagoId,
      monto: montoPagoValor,
      referencia: referenciaPago,
      catPlazoId: catPlazoId,
      periodo: periodoPago
    }
  })

  // Ajustar pagos para que sumen exactamente totalPagar
  const sumaPagos = pagos.reduce((sum, p) => sum + p.monto, 0)
  const diferenciaPagos = redondear(totalPagar - sumaPagos)

  if (diferenciaPagos !== 0) {
    // Ajustar el último pago para que la suma coincida exactamente
    if (pagos.length > 0) {
      // Ajustar el último pago para cubrir la diferencia
      pagos[pagos.length - 1].monto = redondear(pagos[pagos.length - 1].monto + diferenciaPagos)
    } else {
      // Si no hay pagos, crear uno por el total
      // Buscar ID de forma de pago: 14 (Otro) si tiene saldo, 1 (Efectivo) por defecto
      const idFormaPagoDefault = tieneSaldoFavor
        ? (catalogoFormasPago.find(f => f.codigo === '14')?.id || 14)
        : (catalogoFormasPago.find(f => f.codigo === '01')?.id || 1)

      pagos = [{
        catFormaPagoId: idFormaPagoDefault,
        monto: totalPagar,
        referencia: null,
        catPlazoId: null,
        periodo: null
      }]
    }
  } else if (pagos.length === 0) {
    // Crear pago por defecto
    const idFormaPagoDefault = tieneSaldoFavor
      ? (catalogoFormasPago.find(f => f.codigo === '14')?.id || 14)
      : (catalogoFormasPago.find(f => f.codigo === '01')?.id || 1)

    // Para crédito, buscar ID del plazo
    let catPlazoIdDefault = null
    if (condicionOp === 2) {
      const plazoDefault = documento.plazoDefault || '01'
      const plazoItem = catalogoPlazos.find(p => p.codigo === plazoDefault)
      catPlazoIdDefault = plazoItem?.id || parseInt(plazoDefault, 10) || 1
    }

    pagos = [{
      catFormaPagoId: idFormaPagoDefault,
      monto: totalPagar,
      referencia: null,
      catPlazoId: catPlazoIdDefault,
      periodo: condicionOp === 2 ? (documento.periodoDefault || null) : null
    }]
  }

  // ===========================================
  // PASO 12.5: CONSOLIDAR TRIBUTOS PARA RESUMEN
  // ===========================================
  // Según MH: el resumen debe incluir cada tributo consolidado (código, nombre, valor total)
  // SECCIÓN 1: Tributos que afectan valores (IVA, FOVIAL, COTRANS, Turismo)
  // SECCIÓN 3: Tributos INFORMATIVOS (no afectan total a pagar)
  const { tributos: catalogoTributos = [] } = catalogos
  const tributosConsolidados = new Map()

  // Códigos de Sección 3 (informativos) con porcentaje ad-valorem
  const tributosAdValorem = {
    'C5': 0.08,  // Bebidas alcohólicas 8%
    'C6': 0.39,  // Tabaco cigarrillos 39%
    'C7': 1.00   // Tabaco cigarros 100%
  }

  // Códigos de Sección 3 que son solo etiquetas (sin valor calculado)
  const tributosEtiqueta = [
    '19', '28', '31', '32', '33', '34', '35', '36', '37', '38', '39',
    '42', '43', '44', '50', '51', '52', '53', '54', '55', '58',
    '77', '78', '79', '85', '86', '91', '92', 'A1', 'A5', 'A7', 'A9'
  ]

  // Recopilar tributos de todos los ítems
  todosLosItems.forEach(item => {
    if (item.tributos && Array.isArray(item.tributos)) {
      item.tributos.forEach(codigoTributo => {
        if (!tributosConsolidados.has(codigoTributo)) {
          // Buscar descripción en catálogo
          const tributoInfo = catalogoTributos.find(t => t.codigo === codigoTributo)
          tributosConsolidados.set(codigoTributo, {
            codigo: codigoTributo,
            descripcion: tributoInfo?.valor || tributoInfo?.descripcion || `Tributo ${codigoTributo}`,
            valor: 0,
            esInformativo: tributosAdValorem[codigoTributo] !== undefined || tributosEtiqueta.includes(codigoTributo)
          })
        }

        // Sumar el valor del tributo según el código
        const tributoData = tributosConsolidados.get(codigoTributo)

        // ===========================================
        // SECCIÓN 1: Tributos operativos (NO tienen IVA)
        // ===========================================
        // Códigos de Sección 1 que se calculan y suman al total
        const codigosSeccion1 = ['D1', 'C8', '59', '71', 'D4', 'D5', 'C3']

        if (codigoTributo === '20') {
          // IVA 13%: sumar el ivaItem del ítem
          tributoData.valor += item.ivaItem || 0
        } else if (codigosSeccion1.includes(codigoTributo)) {
          // TRIBUTOS SECCIÓN 1: Usar configuración del usuario si existe
          const config = item._tributosAdicionalesConfig?.[codigoTributo]
          const baseCalculo = item._baseParaTributos || 0 // Base sin IVA para Factura

          if (config && config.valor !== undefined && config.valor !== null) {
            // Usuario configuró un valor específico
            if (config.tipoCalculo === 'porcentaje') {
              // Calcular porcentaje sobre base (sin IVA para Sección 1)
              tributoData.valor += baseCalculo * (config.valor / 100)
            } else {
              // Monto fijo (por unidad si aplica, ej: FOVIAL por galón)
              const cantidadUnidades = item.cantidad || 1
              tributoData.valor += config.valor * cantidadUnidades
            }
          } else {
            // FALLBACK: Valores predeterminados si no hay configuración
            if (codigoTributo === 'D1') {
              // FOVIAL: $0.20 por galón
              tributoData.valor += (item.cantidad || 1) * 0.20
            } else if (codigoTributo === 'C8') {
              // COTRANS: $0.10 por galón
              tributoData.valor += (item.cantidad || 1) * 0.10
            } else if (codigoTributo === '59') {
              // Turismo alojamiento 5%
              tributoData.valor += baseCalculo * 0.05
            } else if (codigoTributo === '71') {
              // Turismo salida país: $7.00 fijo por persona
              tributoData.valor += (item.cantidad || 1) * 7.00
            } else if (codigoTributo === 'C3') {
              // IVA exportaciones 0%
              tributoData.valor += 0
            } else if (codigoTributo === 'D4' || codigoTributo === 'D5') {
              // Otros impuestos/tasas: sin config, valor 0 (el usuario debe configurar)
              tributoData.valor += 0
            }
          }
        }
        // ===========================================
        // SECCIÓN 3: Tributos INFORMATIVOS
        // ===========================================
        else if (tributosAdValorem[codigoTributo] !== undefined) {
          // Tributos ad-valorem (C5, C6, C7): calcular sobre diferencial de precios
          // Diferencial = ventaGravada - (PSV * cantidad) si hay PSV
          // Si no hay PSV, usar ventaGravada como base
          const porcentaje = tributosAdValorem[codigoTributo]
          let baseCalculo = item.ventaGravada || 0

          // Si hay PSV (Precio Sugerido Venta), calcular diferencial
          if (item.psv && item.psv > 0) {
            const psvTotal = item.psv * (item.cantidad || 1)
            const diferencialPrecios = (item.ventaGravada || 0) - psvTotal
            // Solo usar diferencial si es positivo
            if (diferencialPrecios > 0) {
              baseCalculo = diferencialPrecios
            }
          }

          tributoData.valor += baseCalculo * porcentaje
        } else if (tributosEtiqueta.includes(codigoTributo)) {
          // Tributos etiqueta: solo informativos, valor = 0
          // No suman ningún valor, solo aparecen en el resumen
          tributoData.valor += 0
        }
      })
    }
  })

  // ===========================================
  // PASO 12.5.1: ACTUALIZAR VALOR DEL TRIBUTO '20' CON IVA NETO
  // ===========================================
  // Para CCF: El tributo '20' (IVA) debe tener el valor de ivaNeto (después de descuento)
  // ya que la consolidación suma ivaItem de cada ítem pero no considera el descuento global
  if (esCCF && tributosConsolidados.has('20')) {
    tributosConsolidados.get('20').valor = ivaNeto
  }

  // Convertir Map a array y redondear valores
  // Los tributos con valor 0 también se incluyen (son informativos)
  // IMPORTANTE según MH Campo 146:
  // - Para CCF: resumen.tributos SÍ incluye '20' (IVA) - el schema fe-ccf-v3.json lo permite
  //   La fórmula es: montoTotalOperacion = subTotal + Σ(tributos.valor)
  // - Para Factura: resumen.tributos NO incluye '20' (el schema fe-fc-v1.json lo excluye en enum)
  //   El subTotal ya incluye IVA, así que tributos solo tiene otros impuestos
  const tributosResumen = Array.from(tributosConsolidados.values())
    .filter(t => esFactura ? t.codigo !== '20' : true) // Solo excluir '20' para Factura
    .map(t => ({
      codigo: t.codigo,
      descripcion: t.descripcion,
      // Sección 3 (informativos): valor DEBE ser 0 en resumen - no afectan montoTotalOperacion
      valor: t.esInformativo ? 0 : redondear(t.valor)
    }))

  // ===========================================
  // PASO 12.6: CALCULAR montoTotalOperacion SEGÚN MH CAMPO 146
  // ===========================================
  // Según documentación oficial MH Campo 146:
  // "Para FE, CCFE y NRE, se deberá colocar la suma del campo 'Sub Total'
  //  (+) los valores contenidos en el campo 'Valor de Tributo'"
  //
  // Fórmula: montoTotalOperacion = subTotal + Σ(tributos.valor)
  //
  // Para Factura: subTotal ya incluye IVA, tributos NO incluye '20'
  // Para CCF: subTotal es NETO, tributos SÍ incluye '20' (IVA)
  const totalTributosResumen = tributosResumen.reduce((sum, t) => sum + t.valor, 0)
  montoTotalOperacion = redondear(subTotal + totalTributosResumen)

  // Recalcular totalPagar con el nuevo montoTotalOperacion
  if (esFactura) {
    totalPagar = redondear(montoTotalOperacion + totalNoGravado - ivaRetenido - retencionRenta)
  } else if (esCCF) {
    totalPagar = redondear(montoTotalOperacion + totalNoGravado - ivaRetenido + ivaPercibido - retencionRenta)
  }

  // Según MH: Si totalNoGravado (abonos) es mayor que montoTotalOperacion,
  // el totalPagar debe ser $0.00 (no puede ser negativo)
  if (totalPagar < 0) {
    totalPagar = 0
  }

  // ===========================================
  // PASO 12.7: AJUSTAR PAGOS CON EL totalPagar FINAL
  // ===========================================
  // Los pagos se calcularon antes con un totalPagar inicial (sin tributos).
  // Ahora que tenemos el totalPagar correcto, ajustamos los montos de los pagos.
  if (pagos.length > 0) {
    const sumaPagosFinal = pagos.reduce((sum, p) => sum + p.monto, 0)
    const diferenciaPagosFinal = redondear(totalPagar - sumaPagosFinal)

    if (diferenciaPagosFinal !== 0) {
      // Ajustar el último pago para cubrir la diferencia
      pagos[pagos.length - 1].monto = redondear(pagos[pagos.length - 1].monto + diferenciaPagosFinal)
    }
  }

  // ===========================================
  // PASO 13: CONSTRUIR DTO FINAL
  // ===========================================
  // Determinar versión del schema según tipo de DTE
  // Factura (01) → version 1, CCF (03) → version 3
  const schemaVersion = tipoDte === '03' ? 3 : 1

  // Obtener tipo de transmisión (1: Normal, 2: Contingencia/Diferida)
  const tipoTransmision = Number(documento.receptor?.tipoTransmision || documento.tipoTransmision) || 1
  // Si es contingencia (2), el modelo también debe ser diferido (2) según MH
  const tipoModelo = tipoTransmision === 2
    ? 2 // Diferido (obligatorio para contingencia)
    : Number(documento.receptor?.tipoModelo || documento.tipoModelo) || 1

  const dto = {
    identificacion: {
      version: schemaVersion,
      ambiente: ambiente,
      tipoDte: tipoDte,
      numeroControl: numeroControl,
      codigoGeneracion: codigoGeneracion,
      tipoModelo: tipoModelo,
      tipoOperacion: tipoTransmision, // 1: Normal, 2: Contingencia
      fechaEmision: fechaEmision,
      horaEmision: horaEmision,
      tipoMoneda: 'USD',  // El validador del backend espera "USD"
      // Campos de contingencia (solo si tipoTransmision = 2)
      tipoContingencia: tipoTransmision === 2
        ? (documento.receptor?.tipoContingencia || documento.tipoContingencia || null)
        : null,
      motivoContingencia: tipoTransmision === 2
        ? (documento.receptor?.motivoContingencia || documento.motivoContingencia || null)
        : null,
      // Si es false, el backend NO crea evento de contingencia automáticamente
      crearEventoAutomatico: tipoTransmision === 2
        ? (documento.receptor?.crearEventoAutomatico ?? true)
        : true
    },

    sucursalId: sucursalId,

    cuerpoDocumento: cuerpoDocumento,

    // Resumen con totales calculados según tipo de DTE
    // NOTA: Para Factura (DTE-01), totalGravada y descuGravada son valores NETOS (sin IVA)
    // ya que el MH valida que totalIva = totalGravada * 0.13
    // Para CCF (DTE-03), todos los valores ya son NETOS por definición
    resumen: {
      // Totales por tipo de venta (para Factura: NETOS, para CCF: NETOS)
      totalNoSuj: redondear(totalNoSuj),
      totalExenta: redondear(totalExenta),
      totalGravada: redondear(totalGravada),
      // Suma de operaciones sin impuestos (MH Sección XVII)
      subTotalVentas: redondear(subTotalVentas),
      // Descuentos globales (para Factura: NETOS, para CCF: NETOS)
      descuNoSuj: redondear(descNoSuj),
      descuExenta: redondear(descExenta),
      descuGravada: redondear(descGravada),
      // Porcentaje global de descuento (0-100, sin símbolo %) - MH Sección XVII
      porcentajeDescuento: porcentajeDescuento,
      // Total de descuentos: Σ(montoDescuento items) + descuNoSuj + descuExenta + descuGravada
      // INFORMATIVO según MH (Sección XVII, punto 4)
      totalDescu: redondear(totalDescuItems + descNoSuj + descExenta + descGravada),
      // IVA: INFORMATIVO en Factura, OPERATIVO en CCF
      totalIva: redondear(totalIva),
      // SubTotal: CON IVA en Factura, SIN IVA en CCF
      subTotal: redondear(subTotal),
      // Monto Total: = subTotal en Factura, = subTotal + IVA en CCF
      montoTotalOperacion: redondear(montoTotalOperacion),
      // Items no afectos
      totalNoGravado: redondear(totalNoGravado),
      totalNoAfectos: redondear(totalNoAfectos),
      totalContribuciones: redondear(totalContribuciones),
      // Retenciones (nombres deben coincidir con ResumenDto del backend)
      // Enviar null cuando es 0 para que el backend NO valide contra el cálculo esperado
      ivaRete1: ivaRetenido > 0 ? redondear(ivaRetenido) : null,
      ivaPerci1: ivaPercibido > 0 ? redondear(ivaPercibido) : null,
      // MH rechaza reteRenta > 0 en Factura (DTE-01), forzar null como seguridad
      reteRenta: esFactura ? null : (retencionRenta > 0 ? redondear(retencionRenta) : null),
      // Total final
      totalPagar: redondear(totalPagar),
      totalLetras: numeroALetras(totalPagar),
      // Saldo a favor del receptor (créditos o pagos previos)
      // Según MH: se registra en NEGATIVO, si no aplica = 0
      // El JSON schema requiere maximum: 0 (debe ser 0 o negativo)
      saldoFavor: documento.saldoFavor ? redondear(-Math.abs(documento.saldoFavor)) : 0,
      condicionOperacion: documento.condicionOperacion || 1,
      pagos: pagos,
      // Número de pago electrónico (opcional, máx 100 caracteres) - nullable según MH
      numPagoElectronico: nullIfEmpty(documento.extension?.numPagoElectronico),
      // Resumen de tributos consolidado (Sección 1 del catálogo)
      // Cada tributo con: código, descripción, valor total
      tributos: tributosResumen.length > 0 ? tributosResumen : null
    },

    observaciones: nullIfEmpty(documento.extension?.observaciones)
  }

  // ===========================================
  // PASO 14: EXTENSIÓN (Responsables de entrega/recepción)
  // ===========================================
  // Mapea los datos de entrega, recepción y placa del vehículo
  // Solo se incluye si hay al menos un campo con valor
  const ext = documento.extension || {}
  const tieneExtension = ext.nombEntrega || ext.docuEntrega ||
                         ext.nombRecibe || ext.docuRecibe ||
                         ext.placaVehiculo || ext.observaciones

  if (tieneExtension) {
    dto.extension = {
      nombEntrega: nullIfEmpty(ext.nombEntrega),
      docuEntrega: nullIfEmpty(ext.docuEntrega),
      nombRecibe: nullIfEmpty(ext.nombRecibe),
      docuRecibe: nullIfEmpty(ext.docuRecibe),
      placaVehiculo: nullIfEmpty(ext.placaVehiculo),
      observaciones: nullIfEmpty(ext.observaciones)
    }
  }

  // ===========================================
  // PASO 15: VENTA A CUENTA DE TERCEROS (Opcional)
  // ===========================================
  // Según MH (Sección XVI): Permite detallar ventas por cuenta de mandantes
  // Reglas importantes:
  //   1. Solo UN mandante por DTE (no se pueden mezclar)
  //   2. No mezclar ventas propias con ventas a cuenta de terceros
  //   3. Si hay varios mandantes, emitir DTEs separados
  // Campos: NIT (14 o 9 dígitos) y Nombre del tercero (3-200 caracteres)
  // IMPORTANTE: Según JSON schema, este campo es REQUERIDO (enviar null si no aplica)
  // Buscar datos de venta a terceros: pueden estar en documento.receptor (emitido por DatosGenerales)
  // o directamente en documento (por compatibilidad)
  const vtFlag = documento.receptor?.ventaTerceros || documento.ventaTerceros
  const vtData = documento.receptor?.tercero || documento.tercero
  if (vtFlag && vtData) {
    // Limpiar guiones del NIT del tercero
    const nitTerceroLimpio = (vtData.documento || '')
      .replace(/-/g, '')

    if (nitTerceroLimpio && vtData.nombre) {
      dto.ventaTercero = {
        nit: nitTerceroLimpio,
        nombre: vtData.nombre
      }
    } else {
      // Si faltan datos, enviar null
      dto.ventaTercero = null
    }
  } else {
    // Según MH: si no hay venta a terceros, enviar null (campo requerido)
    dto.ventaTercero = null
  }

  // ===========================================
  // PASO 16: AGREGAR DATOS DEL RECEPTOR
  // ===========================================
  // El receptor puede venir de dos formas:
  // 1. documento.receptor (objeto anidado)
  // 2. Directamente en documento (campos planos)
  const receptorData = documento.receptor || documento

  // Verificar si es un receptor existente en BD (tiene ID válido)
  if (receptorData?.id && !isNaN(parseInt(receptorData.id))) {
    // RECEPTOR EXISTENTE: Solo enviamos el ID
    // El backend buscará los datos en la tabla de receptores
    dto.receptorId = parseInt(receptorData.id)
  } else if (receptorData?.nombre || receptorData?.numDocumento) {
    // RECEPTOR NUEVO: Enviamos todos los datos
    // El backend creará el receptor automáticamente

    // Limpiar guiones del documento
    // NIT: 0614-010190-101-0 → 06140101901010
    // DUI: 01234567-8 → 012345678
    // NOTA: El componente DatosGeneralesCCF usa 'nit' como nombre de propiedad
    const numDocLimpio = (receptorData.numDocumento || receptorData.nit || receptorData.documento || '')
      .replace(/-/g, '')  // Elimina todos los guiones

    dto.receptor = {
      // Según MH JSON schema: tipoDocumento y numDocumento pueden ser null en Factura
      tipoDocumento: nullIfEmpty(receptorData.tipoDocumento) || '36',  // 36=NIT por defecto si hay datos
      numDocumento: nullIfEmpty(numDocLimpio),
      nrc: receptorData.nrc ? receptorData.nrc.replace(/-/g, '') : null,  // NRC sin guiones
      nombre: nullIfEmpty(receptorData.nombre || receptorData.nombreRazonSocial),

      // Actividad económica del receptor (opcional - nullable según MH)
      codActividad: nullIfEmpty(receptorData.actividadEconomicaCodigo
        || receptorData.actividadEconomica?.codigo),
      descActividad: nullIfEmpty(receptorData.actividadEconomicaDescripcion
        || receptorData.actividadEconomica?.descripcion),

      // Dirección (el objeto puede ser null, y complemento también es nullable)
      // IMPORTANTE: departamento y municipio deben ser strings de 2 dígitos (ej: "06", "14")
      // Si hay complemento (direccion string), enviar siempre aunque no haya depto/municipio
      direccion: (receptorData.departamento || receptorData.direccion) ? {
        departamento: receptorData.departamento ? String(receptorData.departamento).padStart(2, '0') : null,
        municipio: receptorData.municipio ? String(receptorData.municipio).padStart(2, '0') : null,
        complemento: nullIfEmpty(receptorData.direccion)
      } : null,

      telefono: nullIfEmpty(receptorData.telefono),
      correo: nullIfEmpty(receptorData.correo || receptorData.correoElectronico)
    }
  }

  // ===========================================
  // PASO 17: DOCUMENTOS RELACIONADOS
  // ===========================================
  // Se usan cuando la factura hace referencia a otros documentos
  // Ejemplo: Nota de crédito que anula una factura
  // REGLAS MH:
  // - Máximo 50 documentos relacionados
  // - Todos deben ser del MISMO TIPO
  // - OBLIGATORIO para NC (05) y ND (06)
  // - Para FE, CCF, NRE: enviar null si no hay documentos
  const docRelParaDto = Array.isArray(documento.documentosRelacionados)
    ? documento.documentosRelacionados
    : (documento.documentosRelacionados?.relacionados || [])
  if (docRelParaDto.length > 0) {
    dto.documentosRelacionados = docRelParaDto
      .slice(0, 50) // Máximo 50 según MH
      .map(dr => ({
        tipoDocumento: dr.tipoDocumento,       // Tipo del documento relacionado
        tipoGeneracion: dr.tipoGeneracion,     // Cómo fue generado (1=Físico, 2=Electrónico)
        numeroDocumento: dr.numeroDocumento,   // UUID (electrónico) o correlativo (físico)
        fechaEmision: dr.fechaEmision          // Fecha del documento original
      }))
  } else {
    // Según MH: si no hay documentos, enviar null
    dto.documentosRelacionados = null
  }

  // ===========================================
  // PASO 18: OTROS DOCUMENTOS ASOCIADOS (OPCIONAL)
  // ===========================================
  // Según MH (Sección XV): Permite anexar información relevante no tributaria
  // Catálogo CAT-021 "Otros Documentos Asociados":
  //   1 = Emisor (contratos, resoluciones, autorizaciones)
  //   2 = Receptor (pólizas de seguro, etc.)
  //   3 = Médico (nombre, NIT, doc identificación, tipo servicio)
  //   4 = Transportista (solo Factura de Exportación)
  // Máximo 10 documentos según MH (maxItems: 10 en JSON schema)
  const otrosDocsArray = documento.otrosDocumentos
    || (!Array.isArray(documento.documentosRelacionados) ? documento.documentosRelacionados?.otros : null)
    || []
  if (otrosDocsArray.length > 0) {
    dto.otrosDocumentos = otrosDocsArray
      .filter(od => {
        // Para código 3 (Médico): requiere datos de médico
        if (parseInt(od.codDocAsociado) === 3) {
          return od.medico && od.medico.nombre && od.medico.tipoServicio
        }
        // Para códigos 1, 2, 4: requiere descDocumento
        return od.codDocAsociado && od.descDocumento
      })
      .slice(0, 10) // Máximo 10 según MH
      .map(od => {
        const codDoc = parseInt(od.codDocAsociado)

        // Estructura base según JSON schema de MH
        const otroDoc = {
          codDocAsociado: codDoc,
          descDocumento: null,
          detalleDocumento: null,
          medico: null
        }

        if (codDoc === 3) {
          // Código 3 = Médico: campos específicos
          // Según MH: descDocumento y detalleDocumento deben ser null
          const medico = od.medico || {}
          const tipoDocMedico = medico.tipoDocumento // '36' = NIT, '03' = Pasaporte, etc.

          otroDoc.medico = {
            nombre: nullIfEmpty(medico.nombre),
            // Si es NIT (tipo 36), va en "nit"; si no, va en "docIdentificacion"
            nit: tipoDocMedico === '36' ? nullIfEmpty((medico.numDocumento || '').replace(/-/g, '')) : null,
            docIdentificacion: tipoDocMedico !== '36' ? nullIfEmpty(medico.numDocumento) : null,
            tipoServicio: parseInt(medico.tipoServicio) || 1
          }
        } else {
          // Códigos 1, 2, 4: campos genéricos
          // Según MH: medico debe ser null
          otroDoc.descDocumento = nullIfEmpty(od.descDocumento)
          otroDoc.detalleDocumento = nullIfEmpty(od.detalleDocumento)
        }

        return otroDoc
      })
  } else {
    // Según MH: si no hay documentos, enviar null
    dto.otrosDocumentos = null
  }

  // ===========================================
  // PASO 19: APÉNDICE (OPCIONAL)
  // ===========================================
  // Campos adicionales personalizados en formato clave-valor
  // Máximo 10 campos según MH
  const apendiceData = documento.extension?.apendice || documento.apendice || []
  if (apendiceData && apendiceData.length > 0) {
    dto.apendices = apendiceData
      .filter(ap => ap.campo && ap.etiqueta && ap.valor) // Solo enviar si están completos
      .slice(0, 10) // Máximo 10 según MH
      .map(ap => ({
        campo: (ap.campo || '').substring(0, 25),      // Máx 25 caracteres
        etiqueta: (ap.etiqueta || '').substring(0, 50), // Máx 50 caracteres
        valor: (ap.valor || '').substring(0, 150)       // Máx 150 caracteres
      }))
  }

  // ===========================================
  // PASO 20: CAJA Y VENDEDOR (OPCIONALES)
  // ===========================================
  // La caja es el punto de venta desde donde se emite la factura
  if (documento.cajaId) {
    dto.cajaId = parseInt(documento.cajaId)
  }

  // El vendedor es quien realizó la venta (diferente al cajero que emite)
  // El backend registra automáticamente el UsuarioId (cajero) desde el token JWT
  if (documento.vendedorId) {
    dto.vendedorId = parseInt(documento.vendedorId)
  }

  return dto
}

/**
 * =====================================================================================
 * FUNCIÓN: nullIfEmpty
 * =====================================================================================
 *
 * Convierte strings vacíos a null para cumplir con el JSON schema del MH.
 * El Ministerio de Hacienda requiere que los campos opcionales se envíen como
 * null en lugar de strings vacíos ("").
 *
 * @param {any} value - Valor a verificar
 * @returns {any|null} El valor original si tiene contenido, null si está vacío
 *
 * EJEMPLOS:
 * nullIfEmpty("")        → null
 * nullIfEmpty("   ")     → null (solo espacios)
 * nullIfEmpty(null)      → null
 * nullIfEmpty(undefined) → null
 * nullIfEmpty("texto")   → "texto"
 * nullIfEmpty(0)         → 0 (números válidos se mantienen)
 * nullIfEmpty(false)     → false (booleanos válidos se mantienen)
 */
function nullIfEmpty(value) {
  // Si es null o undefined, retornar null
  if (value === null || value === undefined) {
    return null
  }
  // Si es string, verificar si está vacío o solo tiene espacios
  if (typeof value === 'string') {
    return value.trim() === '' ? null : value
  }
  // Para otros tipos (números, booleanos, objetos), retornar el valor original
  return value
}

/**
 * =====================================================================================
 * FUNCIÓN: redondear (2 decimales - RESUMEN)
 * =====================================================================================
 *
 * Redondea un número a 2 decimales con precisión.
 * USAR SOLO PARA CAMPOS DEL RESUMEN según MH (multipleOf: 0.01)
 *
 * Campos del resumen: totalNoSuj, totalExenta, totalGravada, subTotalVentas,
 * descuNoSuj, descuExenta, descuGravada, totalDescu, subTotal, totalIva,
 * montoTotalOperacion, totalNoGravado, totalPagar, tributos.valor, pagos.monto
 *
 * REGLA MH (Sección XXI):
 * -----------------------
 * Si la tercera posición decimal es >= 5, redondear hacia arriba.
 * Si es < 5, mantener el valor de la segunda posición.
 *
 * HOLGURA: El MH permite tolerancia de ±0.01 en campos del resumen.
 *
 * @param {number} num - Número a redondear
 * @returns {number} Número redondeado a 2 decimales
 *
 * EJEMPLO:
 * redondear(5.664) → 5.66
 * redondear(5.665) → 5.67
 * redondear(5.666) → 5.67
 */
function redondear(num) {
  return Math.round((num + Number.EPSILON) * 100) / 100
}

/**
 * =====================================================================================
 * FUNCIÓN: redondear8 (8 decimales - CUERPO DEL DOCUMENTO)
 * =====================================================================================
 *
 * Redondea un número a 8 decimales con precisión.
 * USAR PARA CAMPOS DEL CUERPO DEL DOCUMENTO según MH (multipleOf: 0.00000001)
 *
 * Campos del cuerpo: cantidad, precioUni, montoDescuento, ventaNoSuj, ventaExenta,
 * ventaGravada, psv, noGravado, ivaItem
 *
 * REGLA MH (Sección XXI):
 * -----------------------
 * Si la novena posición decimal es >= 5, redondear hacia arriba la octava.
 * Si es < 5, mantener el valor de la octava posición.
 *
 * EJEMPLO del MH:
 * Cantidad en sistema: 1.87654321987654 → Estructura DTE: 1.87654322
 * Precio en sistema:   3.55555555400000 → Estructura DTE: 3.55555555
 * Venta en sistema:    5.67215366775307 → Estructura DTE: 5.67215367
 *
 * @param {number} num - Número a redondear
 * @returns {number} Número redondeado a 8 decimales
 */
function redondear8(num) {
  return Math.round((num + Number.EPSILON) * 100000000) / 100000000
}

/**
 * =====================================================================================
 * FUNCIÓN: numeroALetras
 * =====================================================================================
 *
 * Convierte un número a su representación en palabras.
 * Requerido por el Ministerio de Hacienda para el campo "totalLetras".
 *
 * EJEMPLOS:
 * ---------
 * numeroALetras(0)        → "CERO DOLARES"
 * numeroALetras(1.00)     → "UN DOLARES"
 * numeroALetras(15.50)    → "QUINCE DOLARES CON 50/100"
 * numeroALetras(100.00)   → "CIEN DOLARES"
 * numeroALetras(1500.25)  → "UN MIL QUINIENTOS DOLARES CON 25/100"
 *
 * ESTRUCTURA DEL ALGORITMO:
 * -------------------------
 * 1. Separar parte entera y decimal
 * 2. Procesar millones si los hay
 * 3. Procesar miles si los hay
 * 4. Procesar centenas/decenas/unidades
 * 5. Agregar "DOLARES"
 * 6. Agregar centavos si los hay
 *
 * @param {number} num - Número a convertir
 * @returns {string} Representación en palabras
 */
function numeroALetras(num) {
  // Arrays con las palabras para cada rango
  // Índice corresponde al valor (unidades[1] = "UN")
  const unidades = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE']

  // Números del 10 al 19 tienen nombres especiales
  const especiales = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISEIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE']

  // Decenas (índice × 10 = valor)
  const decenas = ['', '', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA']

  // Centenas (índice × 100 = valor)
  const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS']

  // Casos base
  if (num === 0) return 'CERO DOLARES'
  if (num < 0) return 'MENOS ' + numeroALetras(Math.abs(num))

  // Separar enteros y decimales
  const entero = Math.floor(num)
  const centavos = Math.round((num - entero) * 100)

  /**
   * Función interna que convierte números de 0-999 a palabras
   * Usa recursión para simplificar el código
   */
  const convertirGrupo = (n) => {
    if (n === 0) return ''
    if (n === 100) return 'CIEN'  // Caso especial: 100 no es "CIENTO"
    if (n < 10) return unidades[n]
    if (n < 20) return especiales[n - 10]  // 10-19

    // 20-29: "VEINTI" + unidad
    if (n < 30) {
      return n === 20 ? 'VEINTE' : 'VEINTI' + unidades[n - 20]
    }

    // 30-99: decena + " Y " + unidad
    if (n < 100) {
      const dec = Math.floor(n / 10)
      const uni = n % 10
      return decenas[dec] + (uni > 0 ? ' Y ' + unidades[uni] : '')
    }

    // 100-999: centena + resto
    const cen = Math.floor(n / 100)
    const resto = n % 100
    return centenas[cen] + (resto > 0 ? ' ' + convertirGrupo(resto) : '')
  }

  let resultado = ''

  // Procesar millones (1,000,000+)
  if (entero >= 1000000) {
    const millones = Math.floor(entero / 1000000)
    resultado += (millones === 1 ? 'UN MILLON ' : convertirGrupo(millones) + ' MILLONES ')
  }

  // Procesar miles (1,000+)
  const resto = entero % 1000000
  if (resto >= 1000) {
    const miles = Math.floor(resto / 1000)
    resultado += (miles === 1 ? 'MIL ' : convertirGrupo(miles) + ' MIL ')
  }

  // Procesar últimos tres dígitos (0-999)
  const ultimosTres = resto % 1000
  if (ultimosTres > 0) {
    resultado += convertirGrupo(ultimosTres)
  }

  // Agregar "DOLARES"
  resultado = resultado.trim() + ' DOLARES'

  // Agregar centavos si hay
  if (centavos > 0) {
    resultado += ' CON ' + convertirGrupo(centavos) + ' CENTAVOS'
  }

  // Agregar sufijo USD según especificación MH (campo 152)
  resultado += ' USD'

  return resultado
}

/**
 * =====================================================================================
 * OBJETO DEL SERVICIO DE FACTURAS
 * =====================================================================================
 *
 * Contiene todos los métodos para interactuar con el API de facturas del backend.
 * Cada método corresponde a una operación CRUD o consulta específica.
 */
export default {
  /**
   * ===========================================================================
   * MÉTODO: create
   * ===========================================================================
   *
   * Crea una nueva factura electrónica.
   *
   * PROCESO COMPLETO:
   * 1. Recibe datos del formulario de emisión
   * 2. Llama a mapearParaAPI() para transformar al formato DTO
   * 3. Envía POST /api/facturas con el DTO
   * 4. El backend valida, genera el JSON del DTE, y guarda en BD
   * 5. Retorna la factura creada con todos sus datos
   *
   * @param {Object} documento - Datos del documento del frontend
   * @param {number} sucursalId - ID de la sucursal que emite
   * @param {string} ambiente - "00" pruebas, "01" producción
   * @param {Object} establecimiento - Datos del punto de venta
   * @param {Object} catalogos - Catálogos para mapeo de IDs
   * @returns {Promise<Object>} Factura creada
   */
  async create(documento, sucursalId, ambiente = '00', establecimiento = null, catalogos = {}) {
    // Transformar datos del frontend al formato del backend
    const dto = mapearParaAPI(documento, sucursalId, ambiente, establecimiento, catalogos)

    // Enviar al backend
    const response = await api.post('/facturas', dto)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: guardarComoPendiente
   * ===========================================================================
   *
   * Guarda la factura como pendiente de envío SIN transmitir a Hacienda.
   * El documento se guarda con EstadoHacienda = "PENDIENTE_ENVIO" y
   * tipoModelo = 1 (normal, NO diferido).
   *
   * DIFERENCIA CON create():
   * - create(): Crea y envía INMEDIATAMENTE a Hacienda
   * - guardarComoPendiente(): Crea pero NO envía, queda pendiente
   *
   * ENDPOINT: POST /api/Facturas/guardar-pendiente
   *
   * @param {Object} documento - Datos del documento del frontend
   * @param {number} sucursalId - ID de la sucursal que emite
   * @param {string} ambiente - "00" pruebas, "01" producción
   * @param {Object} establecimiento - Datos del punto de venta
   * @param {Object} catalogos - Catálogos para mapeo de IDs
   * @returns {Promise<Object>} Factura creada con estado PENDIENTE_ENVIO
   */
  async guardarComoPendiente(documento, sucursalId, ambiente = '00', establecimiento = null, catalogos = {}) {
    // Transformar datos del frontend al formato del backend
    const dto = mapearParaAPI(documento, sucursalId, ambiente, establecimiento, catalogos)

    // Enviar al endpoint de guardar pendiente (NO envía a MH)
    const response = await api.post('/facturas/guardar-pendiente', dto)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: getAll
   * ===========================================================================
   *
   * Obtiene lista paginada de facturas.
   *
   * PAGINACIÓN:
   * -----------
   * En lugar de cargar TODAS las facturas (podrían ser miles),
   * las cargamos por "páginas" de tamaño fijo.
   *
   * @param {Object} params - { pageNumber, pageSize }
   * @returns {Promise<Object>} { items: [...], totalCount, pageNumber, pageSize }
   */
  async getAll(params = {}) {
    // params se envía como query string: /facturas?pageNumber=1&pageSize=10
    const response = await api.get('/facturas', { params })
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: getById
   * ===========================================================================
   *
   * Obtiene una factura específica por su ID.
   *
   * @param {number} id - ID de la factura
   * @returns {Promise<Object>} Factura completa con todos sus datos
   */
  async getById(id) {
    // Template literal para construir la URL: /facturas/123
    const response = await api.get(`/facturas/${id}`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: getByCodigoGeneracion
   * ===========================================================================
   *
   * Busca una factura por su código de generación (UUID).
   *
   * Útil cuando:
   * - Se recibe una referencia externa con el UUID
   * - Se quiere verificar si un documento existe
   *
   * @param {string} codigo - UUID del documento
   * @returns {Promise<Object>} Factura encontrada
   */
  async getByCodigoGeneracion(codigo) {
    const response = await api.get(`/facturas/codigo/${codigo}`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: search
   * ===========================================================================
   *
   * Busca facturas por término de búsqueda.
   * Busca en: número de control, nombre del receptor, etc.
   *
   * @param {string} q - Término de búsqueda
   * @returns {Promise<Array>} Facturas que coinciden
   */
  async search(q, ambiente = null) {
    const params = { q }
    if (ambiente) params.ambiente = ambiente
    const response = await api.get('/facturas/search', { params })
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: getJsonDte
   * ===========================================================================
   *
   * Obtiene el JSON del DTE tal como se envió/enviará a Hacienda.
   *
   * Útil para:
   * - Debugging
   * - Verificar estructura del documento
   * - Reenvío manual a Hacienda
   *
   * @param {number} id - ID de la factura
   * @returns {Promise<Object>} JSON del DTE
   */
  async getJsonDte(id) {
    const response = await api.get(`/facturas/${id}/json-dte`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: anular
   * ===========================================================================
   *
   * Anula (invalida) una factura.
   *
   * PROCESO DE ANULACIÓN:
   * 1. Se envía solicitud con motivo de anulación
   * 2. El backend genera documento de invalidación
   * 3. Se transmite a Hacienda
   * 4. La factura queda marcada como anulada
   *
   * IMPORTANTE: Una factura anulada sigue existiendo pero sin efecto fiscal.
   * No se puede "borrar" una factura, solo anular.
   *
   * @param {number} id - ID de la factura a anular
   * @param {string} motivo - Motivo de la anulación
   * @returns {Promise<Object>} Resultado de la anulación
   */
  async anular(id, motivo) {
    const response = await api.post(`/facturas/${id}/anular`, { motivo })
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: actualizarEstadoHacienda
   * ===========================================================================
   *
   * Actualiza el estado de transmisión a Hacienda.
   *
   * ESTADOS POSIBLES:
   * - PENDIENTE: No se ha enviado a Hacienda
   * - ENVIADO: En proceso de validación
   * - ACEPTADO: Hacienda aceptó el documento (tiene sello)
   * - RECHAZADO: Hacienda rechazó (hay errores)
   *
   * @param {number} id - ID de la factura
   * @param {string} estado - Nuevo estado
   * @param {string} selloRecepcion - Sello de Hacienda (solo si aceptado)
   * @returns {Promise<Object>} Resultado de la actualización
   */
  async actualizarEstadoHacienda(id, estado, selloRecepcion = null) {
    const response = await api.put(`/facturas/${id}/estado-hacienda`, { estado, selloRecepcion })
    return response.data
  },

  async descartarFacturaRechazada(id) {
    const response = await api.delete(`/facturas/${id}/descartar`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: invalidar
   * ===========================================================================
   *
   * Invalida una factura mediante evento oficial del MH.
   *
   * DIFERENCIA CON anular():
   * - anular(): Anulación interna del sistema
   * - invalidar(): Proceso oficial de invalidación ante el Ministerio de Hacienda
   *
   * PROCESO DE INVALIDACIÓN:
   * 1. Se envía solicitud con tipo y motivo de invalidación
   * 2. El backend genera el evento de invalidación para MH
   * 3. Se registra la invalidación en el sistema
   * 4. La factura queda marcada como invalidada oficialmente
   *
   * TIPOS DE INVALIDACIÓN (catálogo CAT-024):
   * - 1: Invalidación del emisor sin aceptación del receptor
   * - 2: Invalidación del emisor a solicitud del receptor
   * - 3: Invalidación del receptor
   *
   * @param {number} id - ID de la factura a invalidar
   * @param {Object} datos - Datos de invalidación
   * @param {number} datos.tipoInvalidacion - Tipo de invalidación (1, 2 o 3)
   * @param {string} datos.motivoInvalidacion - Motivo de la invalidación
   * @param {string} datos.nombreResponsable - Nombre del responsable
   * @param {string} datos.tipoDocResponsable - Tipo documento del responsable
   * @param {string} datos.numDocResponsable - Número documento del responsable
   * @param {string} datos.nombreSolicita - Nombre de quien solicita (tipo 2 y 3)
   * @param {string} datos.tipoDocSolicita - Tipo documento de quien solicita
   * @param {string} datos.numDocSolicita - Número documento de quien solicita
   * @returns {Promise<Object>} Resultado de la invalidación
   */
  async invalidar(id, datos) {
    const response = await api.post(`/facturas/${id}/invalidar`, datos)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: puedeInvalidar
   * ===========================================================================
   *
   * Consulta al backend si una factura puede ser invalidada.
   * El backend verifica:
   * - Si el documento está en estado válido para invalidación
   * - Si está dentro del plazo permitido por el MH según tipo de DTE:
   *   - FE (01): Hasta 3 meses desde fecha de transmisión
   *   - CCF (03): Hasta las 23:59:59 del día siguiente a la transmisión
   *
   * @param {number} id - ID de la factura a verificar
   * @returns {Promise<Object>} Objeto con:
   *   - puedeInvalidar: boolean - Si puede ser invalidada
   *   - razon: string - Razón si no puede invalidarse (opcional)
   *   - fechaLimite: string - Fecha límite para invalidar (ISO format)
   *   - tiempoRestante: Object - Tiempo restante desglosado (opcional)
   */
  async puedeInvalidar(id) {
    const response = await api.get(`/facturas/${id}/puede-invalidar`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: consultarEstado
   * ===========================================================================
   *
   * Consulta el estado actual de un DTE en el Ministerio de Hacienda.
   *
   * ENDPOINT: POST /api/Hacienda/consultar-dte
   *
   * Útil para:
   * - Verificar si un DTE diferido ya fue procesado
   * - Actualizar el estado local con el estado real en MH
   *
   * @param {number} id - ID de la factura
   * @returns {Promise<Object>} Estado actualizado del DTE
   */
  async consultarEstado(id) {
    // Primero obtener los datos de la factura para tener el codigoGeneracion y tipoDte
    const factura = await this.getById(id)
    if (!factura) {
      throw new Error('Factura no encontrada')
    }

    const request = {
      emisorId: factura.emisorId,
      codigoGeneracion: factura.codigoGeneracion,
      tipoDte: factura.tipoDte
    }

    const response = await api.post('/Hacienda/consultar-dte', request)

    // Si la respuesta incluye estado, actualizar la factura local
    if (response.data?.estadoHacienda) {
      // Actualizar el estado en la base de datos local
      await this.actualizarEstadoHacienda(id, response.data.estadoHacienda, response.data.selloRecepcion)
    }

    return response.data
  },

  // ===========================================
  // EXPORTAR FUNCIONES AUXILIARES
  // ===========================================
  // Estas funciones también se exportan por si otros módulos las necesitan
  mapearParaAPI,
  numeroALetras
}
